import { useMutation, useQuery } from '@apollo/client';
import {
  COMMON_CONSTANTS,
  DateTimeFormatter,
  Loader,
  toast,
  useUserMSPCheckHook,
  withErrorBoundary,
  getTranslatedText
} from '@nile/components';
import {
  GET_ALL_APPROVAL_STATUS,
  RESEND_APPROVAL,
  UPDATE_APPROVAL
} from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Button,
  ButtonGroup,
  Card,
  Heading,
  TextStyle
} from '@superops/ui-design-system';
import dayjs from 'dayjs';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useIntl } from 'react-intl';

const ApprovalDetailsSheet = (props) => {
  const { approvalList, onCancel, onResendApproval, onUpdateApproval } = props;
  const isMSP = useUserMSPCheckHook();
  const intl = useIntl();
  const [updateItemId, setUpdateItemId] = useState(null);
  const [approvalStatusData, setApprovalStatusData] = useState(null);

  const {
    data: approvalStatuses,
    loading: statusLoading,
    error: statusError
  } = useQuery(GET_ALL_APPROVAL_STATUS, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    onCompleted(res) {
      if (res?.getAllApprovalStatus) {
        setApprovalStatusData(res?.getAllApprovalStatus);
      }
    }
  });

  const getStatusName = (state) =>
    approvalStatusData?.find((el) => el.state === state)?.name;

  const allStatuses = approvalStatusData
    ? {
        sentStatus: getStatusName('CREATE'),
        approvedStatus: getStatusName('APPROVE'),
        rejectedStatus: getStatusName('REJECT'),
        cancelledStatus: getStatusName('CANCEL'),
        needMoreInfoStatus: getStatusName('NEED_MORE_INFORMATION')
      }
    : {};

  const [updateApproval, { loading: updateLoading }] = useMutation(
    UPDATE_APPROVAL,
    {
      onCompleted({ updateApproval }) {
        if (onUpdateApproval) {
          onUpdateApproval(res);
        }
      }
    }
  );

  const [resendApproval, { loading: resendLoading }] = useMutation(
    RESEND_APPROVAL
  );
  const resendApprovalFn = (id) => {
    resendApproval({
      variables: {
        approvalId: id
      }
    }).then((res) => {
      toast('success', 'Approval sent successfully');
      setUpdateItemId(null);
      if (onResendApproval) {
        onResendApproval(res);
      }
    });
  };

  const updateApprovalFn = (id, status) => {
    const allApprovalStatuses = approvalStatuses.getAllApprovalStatus;
    const updatedStatus = allApprovalStatuses.find((el) => el.state === status);
    updateApproval({
      variables: {
        approvalId: id,
        approvalInput: {
          status: updatedStatus.name
        }
      }
    }).then((res) => {
      toast('success', 'Approval cancelled successfully');
    });
  };
  const textMap = {};
  const svgMap = {};
  textMap[allStatuses.sentStatus] = getTranslatedText(
    intl,
    'ticket.runbooks.approvals.waitingForApprovalFrom.label'
  );
  textMap[allStatuses.approvedStatus] = getTranslatedText(
    intl,
    'ticket.details.approvalBubble.approved'
  );
  textMap[allStatuses.rejectedStatus] = getTranslatedText(
    intl,
    'ticket.runbooks.approvals.rejectedBy.label'
  );
  textMap[allStatuses.cancelledStatus] = getTranslatedText(
    intl,
    'ticket.runbooks.approvals.cancelledBy.label'
  );
  textMap[allStatuses.needMoreInfoStatus] = getTranslatedText(
    intl,
    'ticket.details.runbook.needMoreInfo'
  );

  svgMap[allStatuses.needMoreInfoStatus] = 'Conversation';
  svgMap[allStatuses.approvedStatus] = 'Check';
  svgMap[allStatuses.rejectedStatus] = 'RejectedCircle';

  const renderActionButtons = (approvalItem, showIcon) => {
    return (
      <div className="flex jend mt10">
        {allStatuses.approvedStatus !== approvalItem.status && isMSP && (
          <ButtonGroup>
            <Button
              loading={
                approvalItem.approvalId === updateItemId && updateLoading
              }
              disabled={
                approvalItem.approvalId === updateItemId && resendLoading
              }
              onClick={() => {
                setUpdateItemId(approvalItem.approvalId);
                updateApprovalFn(approvalItem.approvalId, 'CANCEL');
              }}
            >
              Cancel
            </Button>

            <Button
              loading={
                approvalItem.approvalId === updateItemId && resendLoading
              }
              disabled={
                approvalItem.approvalId === updateItemId && updateLoading
              }
              onClick={() => {
                setUpdateItemId(approvalItem.approvalId);
                resendApprovalFn(approvalItem.approvalId);
              }}
              icon={showIcon && <SVGIcon name="SendWhite" />}
            >
              Resend
            </Button>
          </ButtonGroup>
        )}
      </div>
    );
  };

  if (statusLoading) {
    <div className="vw40 p15 full-height bg-white scroll">
      <div className="flex actr jsctr w100">
        <Loader />
      </div>
    </div>;
  }

  return (
    <div className="vw40 p15 full-height bg-white scroll">
      <div className="flex center mb20">
        <Heading element="h2" weight="SemiBold">
          <FormattedMessage id="ticket.runbook.approvals.header" />
        </Heading>
        <div className="mlauto pointer" onClick={onCancel}>
          <SVGIcon name="Close" />
        </div>
      </div>
      <div className="p10">
        <Card padding="sizeSmall">
          <div
            className="flex col approvals-list-cards"
            id=" approvals-list-cards"
          >
            {approvalList.map((el) => {
              return (
                <div id="approval-wrapper">
                  <div className="flex center mb20">
                    {svgMap?.[el.status] && (
                      <SVGIcon name={svgMap?.[el.status]} />
                    )}
                    <div className={svgMap?.[el.status] ? 'ml8' : ''}>
                      <TextStyle weight="SemiBold">
                        {textMap[el.status]}
                      </TextStyle>
                    </div>
                    <div className="ml-16">
                      <Avatar
                        size="small"
                        initials={`${el.approver.firstName[0]}${el.approver
                          .lastName?.[0] || ''}`}
                      />
                    </div>
                    <div className="ml8">{el.approver.name}</div>
                    <div className="mlauto">
                      <DateTimeFormatter
                        type="dateTime"
                        date={el.actionTakenOn || el.sentOn}
                      />
                    </div>
                  </div>
                  {(el?.comment || el?.additionalMessage) && (
                    <>
                      <Card
                        noShadow
                        variant={el?.comment ? 'attention' : 'secondaryCard'}
                      >
                        <div className="p10">
                          <TextStyle>
                            {el?.comment || el?.additionalMessage}
                          </TextStyle>
                        </div>
                      </Card>
                    </>
                  )}
                  {renderActionButtons(el, true)}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default withErrorBoundary(ApprovalDetailsSheet);
